<style scoped lang="scss">
@import './DropdownAccountStyle.scss';
</style>

<template>
<div class="flex items-center">
  <client-only>
    <div :key="forceRerender" v-show="is_verified || userInfo.user_id" class="mr-30">
      <tf-chat-notify />
    </div>
  </client-only>
  <div
    v-if="is_verified || userInfo.user_id"
    class="right-account flex items-center"
    v-ClickOutside="closeAccountDropdown">
    <button
      type="button"
      ref="triggerEl"
      class="right-account--button-login"
      title="View account"
      aria-label="View account"
      aria-haspopup="listbox"
      :aria-expanded="showAccountDropdown ? 'true' : 'false'"
      @click="openAccountDropdown"
      @keydown.down.prevent="onTriggerArrow"
      @keydown.up.prevent="onTriggerArrow"
    >
      <div id="circle_avatar" class="circle" >
        <template v-if='!avatar'><span id="span_name">{{ getUserName }}</span></template>
        <template v-if='avatar'><img id="user_avatar" class='img-cover' :src="avatar" alt="User login icon" width='24' height='24' /></template>
      </div>
      <img :src="`${$assetsCdn}/vectors/general/chevron-down.svg`" width='8' height='5' alt='chevron down' />
    </button>
    <div
      ref="panelEl"
      :class="{
        active: showAccountDropdown,
        'right-account--dropdown': true,
      }"
      role="listbox"
      aria-label="Account menu"
      @keydown="onMenuKeydown"
    >
      <ul>
        <li>
          <router-link :to="{name: goToEnquiries()}" role="option" tabindex="-1" :aria-selected="isCurrent(goToEnquiries()) ? 'true' : 'false'" title="Go to page: Enquiries" aria-label="Go to page: Enquiries">
            <span>Enquiries</span>
          </router-link>
        </li>
        <li>
          <router-link :to="{name: goToBookings()}" role="option" tabindex="-1" :aria-selected="isCurrent(goToBookings()) ? 'true' : 'false'" title="Go to page: Bookings" aria-label="Go to page: Bookings">
            <span>Bookings</span>
          </router-link>
        </li>
        <li v-if="domain==='agent'">
          <router-link :to="{name: goToFAQ()}" role="option" tabindex="-1" :aria-selected="isCurrent(goToFAQ()) ? 'true' : 'false'" title="Go to page: FAQ" aria-label="Go to page: FAQ">
            <span>FAQ</span>
          </router-link>
        </li>
        <li>
          <router-link :to="{name: goToAccountSettings()}" role="option" tabindex="-1" :aria-selected="isCurrent(goToAccountSettings()) ? 'true' : 'false'" title="Go to page: Account Settings" aria-label="Go to page: Account Settings">
            <span>Account Settings</span>
          </router-link>
        </li>
        <li @click="handleLogout">
          <div
            class="hand"
            role="option"
            tabindex="-1"
            aria-selected="false"
            title="Go to page: Logout"
            aria-label="Go to page: Logout"
            @keydown.enter.prevent="handleLogout"
            @keydown.space.prevent="handleLogout"
          >
            <span>Logout</span>
          </div>
        </li>
      </ul>
    </div>
  </div>

  <div
    class="right-account"
    v-ClickOutside="closeAccountDropdown"
    v-else
  >
    <button
      type="button"
      ref="triggerEl"
      class="right-account--button"
      title="View account"
      aria-label="View account"
      aria-haspopup="listbox"
      :aria-expanded="showAccountDropdown ? 'true' : 'false'"
      @click="openAccountDropdown"
      @keydown.down.prevent="onTriggerArrow"
      @keydown.up.prevent="onTriggerArrow"
    >
      <img :src="`${$assetsCdn}/icons/user.svg`" class='mr-10' alt="User login icon" width='24' height='24'/>

      <img :src="`${$assetsCdn}/vectors/general/chevron-down.svg`" width='8' height='5' alt='chevron down' />
    </button>
    <div
      ref="panelEl"
      :class="{
        active: showAccountDropdown,
        'right-account--dropdown': true,
      }"
      role="listbox"
      aria-label="Account menu"
      @keydown="onMenuKeydown"
    >
      <ul>
        <li>
          <div
            class="hand"
            role="option"
            tabindex="-1"
            aria-selected="false"
            @click="toLogin"
            @keydown.enter.prevent="toLogin"
            @keydown.space.prevent="toLogin"
            title="Go to page: Login"
            aria-label="Go to page: Login"
          >
            <span>Login</span>
          </div>
        </li>
        <li>
          <div
            class="hand"
            role="option"
            tabindex="-1"
            aria-selected="false"
            @click="toSignUp"
            @keydown.enter.prevent="toSignUp"
            @keydown.space.prevent="toSignUp"
            title="Go to page: Signup"
            aria-label="Go to page: Signup"
          >
            <span>Sign Up</span>
          </div>
        </li>
      </ul>
    </div>
  </div>
  <PopupCommon
    :modalTitle="'Enquiry Details'"
    :modalActive="showModalEnquiryDetail"
    @update:close="closeModalEnquiryDetail()"
    maxWidth='600'
    overflowBody
  >
    <template #content>
      <AppTable :datas="enquiryDetailList" type="vertical"></AppTable>
    </template>
  </PopupCommon>
</div>
</template>

<script>
import ClickOutside from '~/directives/ClickOutside'
import { ref, computed, onMounted, /*watchEffect,*/ watchPostEffect, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
// import { getCookie, setCookie } from '~/mixins/cookie'
import { useGetCookie } from '~/composables/common/useCustomCookie'
import { publicDomain } from '~/helper'
import { TfChat, TfChatNotify } from 'tf-chat'
import { pages } from '~/constant/redirectLogout'
import AppTable from '~/components/ui/table/AppTable'
import PopupCommon from '~/components/ui/popup/PopupCommon'
import { useAuthStore } from '~/store/pinia/auth'
import { useGeneralInfoStore } from '~/store/pinia/generalInfo'

export default {
  props: {
    theme: String
  },
  directives: {
    ClickOutside,
  },
  components:{
    TfChatNotify,
    AppTable,
    PopupCommon
  },
  setup(){
    const loaded = ref(false)
    const authStore = useAuthStore()
    const router = useRouter()
    const route = useRoute()
    const generalStore = useGeneralInfoStore()

    const showAccountDropdown = ref(false)
    const openAccountDropdown = () => {
      showAccountDropdown.value = !showAccountDropdown.value
    }
    const closeAccountDropdown = () => {
      showAccountDropdown.value = false
    }

    // --- Dropdown-menu ARIA / roving keyboard focus (V15) ---
    const triggerEl = ref(null)
    const panelEl = ref(null)
    const activeIndex = ref(0)
    const isCurrent = (name) => route.name === name
    const menuRows = () => Array.from(panelEl.value?.querySelectorAll('[role="option"]') || [])
    const setActive = (i) => {
      const rows = menuRows()
      if (!rows.length) return
      const n = rows.length
      activeIndex.value = ((i % n) + n) % n
      rows.forEach((r, idx) => r.setAttribute('tabindex', idx === activeIndex.value ? '0' : '-1'))
      rows[activeIndex.value].focus()
    }
    const focusInitialRow = () => nextTick(() => {
      const rows = menuRows()
      const sel = rows.findIndex((r) => r.getAttribute('aria-selected') === 'true')
      setActive(sel >= 0 ? sel : 0)
    })
    const onTriggerArrow = () => {
      if (!showAccountDropdown.value) showAccountDropdown.value = true
      focusInitialRow()
    }
    const onMenuKeydown = (e) => {
      const rows = menuRows()
      const n = rows.length
      const cur = rows.findIndex((r) => r === document.activeElement)
      switch (e.key) {
        case 'ArrowDown': e.preventDefault(); setActive((cur < 0 ? -1 : cur) + 1); break
        case 'ArrowUp': e.preventDefault(); setActive((cur < 0 ? 0 : cur) - 1); break
        case 'Home': e.preventDefault(); setActive(0); break
        case 'End': e.preventDefault(); setActive(n - 1); break
        case 'Escape': e.preventDefault(); closeAccountDropdown(); triggerEl.value?.focus(); break
        default: break
        // Enter / Space activate the focused row (router-link natively; .hand via @keydown)
      }
    }

    const domain = publicDomain()

    const toSignUp = () =>{
      // if (domain === 'agent') {
      //   router.push({ name: 'signup-agent' })
      // } else{
        router.push({ name: 'signup-traveller' })
      // }
    }

    const toLogin = () =>{
      // if (domain === 'agent') {
      //   router.push({ name: 'home' })
      // } else{
        router.push({ name: 'login-traveller' })
      // }
    }
    const forceRerender = ref(0)
    //const is_admin = useGetCookie("is_admin")
    const is_verified = useGetCookie("is_verified")
    const dj_xxer_hash = useGetCookie("dj_xxer_hash", 365)
    const dj_uid = useGetCookie("dj_uid", 365)
    const firstName = ref('')
    const lastName = ref('')
    const avatar = ref(null)
    const getUserName = ref('')
    const tokenChat = ref('')
    const userInfo = computed(() => authStore.getUserInfo)

    const userLogin = async () => {
      if(is_verified.value || userInfo.value.user_id){
        // const checkHash = getCookie("dj_xxer_hash=")
        try{
          await authStore.verify()
          if(userInfo.value.status === 200) {
            firstName.value = userInfo.value.firstName
            lastName.value = userInfo.value.lastName
            avatar.value = userInfo.value.avatar
            getUserName.value = firstName.value.charAt(0).toUpperCase() + lastName.value.charAt(0).toUpperCase() || ''
            loaded.value = true
            tokenChat.value = userInfo.value.token
            let sdkToken = tokenChat.value
            sdkToken && await TfChat.init(sdkToken)
            if(!dj_xxer_hash.value){
              // setCookie('dj_xxer_hash',  userInfo.value.hash, COOKIE_DURATION)
              dj_xxer_hash.value = userInfo.value.hash
            }
          } else {
            await authStore.logout()
            is_verified.value = null
            dj_xxer_hash.value = null
            dj_uid.value = null
            //is_admin.value = null
            window.location.reload()
            localStorage.removeItem('chat_array')
            sessionStorage.removeItem('auth_path')
          }
        } catch(err){
          console.log('userLogin error: ',err)
          if((err?.status || err?.response?.status) === 401){
            // const COOKIE_DURATION = 0
            await authStore.logout()
            // setCookie('is_verified', 0, COOKIE_DURATION)
            // setCookie('dj_xxer_hash', 0, COOKIE_DURATION)
            is_verified.value = null
            dj_xxer_hash.value = null
            dj_uid.value = null
            //is_admin.value = null
            window.location.reload()
            localStorage.removeItem('chat_array')
            sessionStorage.removeItem('auth_path')
          }
        }
      }
    }

    onMounted(async ()=>{
      await nextTick(async()=> await userLogin())
    })

    const handleLogout = async () =>{
      // const COOKIE_DURATION = 0
      await authStore.logout()
      // setCookie('is_verified', 0, COOKIE_DURATION);
      // setCookie('dj_xxer_hash', 0, COOKIE_DURATION);
      is_verified.value = null
      dj_xxer_hash.value = null
      dj_uid.value = null
      //is_admin.value = null
      localStorage.removeItem('chat_array');
      sessionStorage.removeItem('auth_path');
      sessionStorage.removeItem('dj_uid');
      if (pages.includes(route.name)) {
        window.location.href = '/'
      } else {
        window.location.reload()
      }
    }

    const enquiryDetailList = computed(() => generalStore.enquiryDetail.enquiryDetailList.enquiry_infos)

    const showModalEnquiryDetail = computed(() => generalStore.enquiryDetail.showModalEnquiryDetail)

    const closeModalEnquiryDetail = () => {
      let data = {
        showModalEnquiryDetail: false,
        enquiryDetailList: {}
      }

      generalStore.setEnquiryDetail(data)
    }

    watchPostEffect(()=>{
      if(showModalEnquiryDetail.value){
        document.getElementById('cover-chat-tf').style.zIndex = '9997'
      }
    })

    const goToAccountSettings = () => {
      return 'account-settings'
    }
    const goToEnquiries = () => {
      return 'enquiries-listing'
    }
    const goToBookings = () => {
      return 'bookings-listing'
    }
    const goToFAQ = () => {
      return 'faq-agent'
    }

    watch(userInfo, async () => {
      await userLogin()
    })

    return {
      loaded,
      showAccountDropdown,
      openAccountDropdown,
      closeAccountDropdown,
      triggerEl,
      panelEl,
      isCurrent,
      onTriggerArrow,
      onMenuKeydown,
      toSignUp,
      toLogin,
      is_verified,
      avatar,
      getUserName,
      handleLogout,
      goToAccountSettings,
      goToEnquiries,
      goToBookings,
      goToFAQ,
      domain,
      forceRerender,
      showModalEnquiryDetail,
      enquiryDetailList,
      userInfo,
      closeModalEnquiryDetail
    }
  }
}
</script>
